import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alerta, Botao, Caminho, Campo, Cartao, Selo, Tabela } from "@plantus/ui";
import { recipientes as api, ErroDaApi } from "@plantus/api-client";
import type {
  OcupacaoNoHistorico,
  OcupacoesSemRecipiente,
  Recipiente,
  TipoDeRecipiente,
} from "@plantus/api-client";

import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { caminhoDaRota } from "../../../nucleo/navegacao/caminho";
import { ROTAS } from "../../../nucleo/navegacao/rotas";
import { ROTULO_DO_ESTADO, TOM_DO_ESTADO } from "./estadoDoRecipiente";
import { colunas } from "./PaginaRecipientesColunas";

import "./recipientes.css";

const TIPOS: { valor: TipoDeRecipiente; rotulo: string }[] = [
  { valor: "BOMBONA", rotulo: "Bombona" },
  { valor: "TAMBOR", rotulo: "Tambor" },
  { valor: "IBC", rotulo: "IBC" },
];

/**
 * O parque de recipientes — os tambores, as bombonas, os IBCs.
 *
 * <h2>🔴 Esta é a única tela de dado regulado SEM seletor de empresa</h2>
 *
 * E é decisão, não esquecimento. O recipiente é a primeira entidade regulada do
 * sistema <b>sem CNPJ</b> (ADR-0012 item 2): o mesmo tambor recebe material das
 * três empresas ao longo da vida, e é exatamente isso que o Art. 192 pergunta —
 * <i>"o que já esteve nesta bombona antes?"</i>.
 *
 * <p>Um seletor aqui recortaria o <b>histórico</b>, e um histórico por empresa
 * responderia <i>"nada esteve aqui antes"</i> para o tambor que teve outro
 * material na semana passada. Que é a resposta que produz contaminação cruzada.
 *
 * <p>A empresa volta em <b>um</b> lugar desta tela: a contagem do mutirão, que
 * não é sobre o ativo — é sobre <b>ocupações</b>, e ocupação tem dono.
 *
 * <h2>Identidade primeiro, dossiê depois</h2>
 *
 * O botão que cria um recipiente não pede nada: o código é gerado, o estado
 * nasce em qualificação e o dossiê fica em branco. É o ADR-0012 item 6 — dado
 * de ativo digitado com o caminhão esperando é dado que ninguém confia depois.
 *
 * <h2>O que esta tela NÃO faz</h2>
 *
 * Não registra limpeza, não avalia compatibilidade e não decide se um tambor
 * pode ser reutilizado. É a #31, travada por um POP que não existe — e uma tela
 * que oferecesse esses botões pareceria controle implantado numa inspeção.
 */
export function PaginaRecipientes() {
  const { lista: empresas } = usarEmpresas();
  const navegar = useNavigate();

  const [parque, definirParque] = useState<Recipiente[]>([]);
  const [incluirAposentados, definirIncluirAposentados] = useState(false);
  const [selecionado, definirSelecionado] = useState<string | null>(null);
  const [historico, definirHistorico] = useState<OcupacaoNoHistorico[]>([]);
  const [mutirao, definirMutirao] = useState<OcupacoesSemRecipiente | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [salvando, definirSalvando] = useState(false);

  // O dossiê em edição
  const [tipo, definirTipo] = useState<TipoDeRecipiente | "">("");
  const [construcao, definirConstrucao] = useState("");
  const [capacidade, definirCapacidade] = useState("");
  const [massaMaxima, definirMassaMaxima] = useState("");
  const [tara, definirTara] = useState("");
  const [comissionadoEm, definirComissionadoEm] = useState("");
  const [motivo, definirMotivo] = useState("");

  /*
   * O mutirão é do GRUPO: a contagem soma as produtoras, e é uma requisição só
   * — o mesmo argumento da lista consolidada de lotes. A distribuidora fica
   * fora porque ela não recebe matéria-prima, e portanto não tem ocupação.
   */
  const produtoras = useMemo(
    () => empresas.filter((e) => e.papel === "PRODUTORA").map((e) => e.id),
    [empresas],
  );
  const chaveDasEmpresas = produtoras.join(",");

  const carregar = useCallback(() => {
    api
      .listar(incluirAposentados)
      .then(definirParque)
      .catch(() => definirErro("Não foi possível carregar os recipientes."));
  }, [incluirAposentados]);

  useEffect(() => carregar(), [carregar]);

  useEffect(() => {
    const ids = chaveDasEmpresas === "" ? [] : chaveDasEmpresas.split(",");
    api.ocupacoesSemRecipiente(ids).then(definirMutirao).catch(() => undefined);
  }, [chaveDasEmpresas, parque]);

  useEffect(() => {
    if (selecionado === null) {
      definirHistorico([]);
      return;
    }
    api
      .ficha(selecionado)
      .then((ficha) => {
        definirHistorico(ficha.historico);
        definirTipo(ficha.recipiente.tipo ?? "");
        definirConstrucao(ficha.recipiente.construcao ?? "");
        definirCapacidade(ficha.recipiente.capacidadeNominalEmLitros ?? "");
        definirMassaMaxima(ficha.recipiente.massaMaximaDeTrabalhoEmKg ?? "");
        definirTara(ficha.recipiente.taraEmKg ?? "");
        definirComissionadoEm(ficha.recipiente.comissionadoEm ?? "");
      })
      .catch(() => definirErro("Não foi possível carregar a ficha do recipiente."));
  }, [selecionado]);

  const emFoco = parque.find((r) => r.id === selecionado) ?? null;

  async function executar(acao: () => Promise<unknown>) {
    definirErro(null);
    definirSalvando(true);
    try {
      await acao();
      carregar();
      if (selecionado !== null) {
        const ficha = await api.ficha(selecionado);
        definirHistorico(ficha.historico);
      }
    } catch (falha) {
      // `422` é regra de negócio, e a mensagem foi escrita para ser lida.
      definirErro(
        falha instanceof ErroDaApi && falha.status === 422
          ? falha.message
          : "Não foi possível concluir a operação.",
      );
    } finally {
      definirSalvando(false);
    }
  }

  return (
    /* Tela DENSA — sete colunas de parque de ativos. Ver "A LEI DE LARGURA". */
    <div className="recipientes" data-largura="densa">
      <header className="recipientes__topo">
        <div>
          {/* Os três níveis eram uma string escrita à mão; agora vêm do mapa,
              e o degrau "Matérias-primas" LEVA de volta à lista — que era o
              caminho que esta tela não tinha. */}
          <Caminho passos={caminhoDaRota(ROTAS.recipientes)} aoNavegar={navegar} />
          <h1 className="recipientes__titulo">Recipientes</h1>
          <p className="recipientes__subtitulo">
            O tambor, a bombona, o IBC — o <strong>ativo físico</strong>, que dura
            anos e atravessa lotes. Ele <strong>não pertence a um CNPJ</strong>:
            quem diz de quem era o material é a ocupação.
          </p>
        </div>
        <Botao onClick={() => void executar(() => api.identificar())} carregando={salvando}>
          Identificar novo recipiente
        </Botao>
      </header>

      {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

      {/* ── o tamanho do mutirão ──────────────────────────────────────
          Nulo é o estado honesto, E É CONTÁVEL. Enquanto este número não
          for zero, o sistema conhece um parque menor que o real — e a
          diferença é invisível em qualquer outra tela. */}
      {mutirao !== null && mutirao.semRecipiente > 0 && (
        <Alerta tom="atencao" titulo="Ocupações sem recipiente identificado">
          <strong>
            {mutirao.semRecipiente} de {mutirao.total}
          </strong>{" "}
          ocupações ainda não sabem em qual tambor estão. Não é dado perdido: é
          dado que <strong>nunca existiu</strong> — o parque não tinha plaqueta
          quando o material foi recebido. O vínculo é trabalho de campo, com a
          plaqueta na mão.
        </Alerta>
      )}

      <Cartao titulo="O parque">
        <div className="recipientes__acoes">
          <label className="recipientes__inclusao">
            <input
              type="checkbox"
              checked={incluirAposentados}
              onChange={(e) => definirIncluirAposentados(e.target.checked)}
            />
            {/* Aposentado sai da lista de TRABALHO e continua no banco:
                some da tela é diferente de some do banco. */}
            <span>Mostrar aposentados</span>
          </label>
        </div>

        {parque.length === 0 ? (
          <p className="recipientes__vazio">
            Nenhum recipiente cadastrado. O parque começa vazio de propósito: o
            cadastro é <strong>trabalho de campo</strong> — alguém vai ao galpão,
            aplica a plaqueta e registra o tambor —, e não linha de migration.
          </p>
        ) : (
          <Tabela
            colunas={colunas(definirSelecionado)}
            linhas={parque}
            chave={(r) => r.id}
            legenda={`${parque.length} recipiente(s)${
              incluirAposentados ? " · incluindo aposentados" : ""
            }`}
          />
        )}
      </Cartao>

      {emFoco !== null && (
        <Cartao titulo={`Recipiente ${emFoco.codigo}`}>
          <h2 className="recipientes__ficha-titulo">
            {emFoco.codigo}{" "}
            <Selo ativo={TOM_DO_ESTADO[emFoco.estado]}>
              {ROTULO_DO_ESTADO[emFoco.estado]}
            </Selo>
          </h2>

          {emFoco.motivoDoEstado !== null && (
            <p className="recipientes__motivo">
              <span className="recipientes__rotulo">Motivo do estado atual</span>{" "}
              {emFoco.motivoDoEstado}
            </p>
          )}

          {/* ── o dossiê ────────────────────────────────────────────────
              ⚠️ TRÊS GRANDEZAS, e cada uma com a unidade no rótulo. A
              quarta — a quantidade acondicionada — é da OCUPAÇÃO, e não
              aparece aqui: um tambor de 50 L com massa máxima de 48 kg
              contendo 47,185 kg de óleo são três números diferentes sobre
              o mesmo objeto. */}
          <div className="recipientes__grade">
            <div className="ui-campo">
              <label className="ui-campo__rotulo" htmlFor="tipo-do-recipiente">
                Tipo
              </label>
              <select
                id="tipo-do-recipiente"
                className="ui-campo__entrada"
                value={tipo}
                onChange={(e) => definirTipo(e.target.value as TipoDeRecipiente | "")}
              >
                <option value="">Selecione…</option>
                {TIPOS.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </div>

            <Campo
              rotulo="Material de construção"
              value={construcao}
              onChange={(e) => definirConstrucao(e.target.value)}
              dica="Polietileno, inox 316, aço carbono. Texto livre até a Qualidade fechar o vocabulário."
            />
            <Campo
              rotulo="Capacidade nominal (L)"
              inputMode="decimal"
              value={capacidade}
              onChange={(e) => definirCapacidade(e.target.value.replace(",", "."))}
              dica="Do recipiente, em litros — não é a quantidade que está dentro."
            />
            <Campo
              rotulo="Massa máxima de trabalho (kg)"
              inputMode="decimal"
              value={massaMaxima}
              onChange={(e) => definirMassaMaxima(e.target.value.replace(",", "."))}
              dica="Do recipiente, em quilos. Grandeza diferente da capacidade."
            />
            <Campo
              rotulo="Tara (kg)"
              inputMode="decimal"
              value={tara}
              onChange={(e) => definirTara(e.target.value.replace(",", "."))}
              dica="A massa do recipiente vazio."
            />
            <Campo
              rotulo="Comissionado em"
              type="date"
              value={comissionadoEm}
              onChange={(e) => definirComissionadoEm(e.target.value)}
              dica="Quando o ativo entrou em serviço."
            />
          </div>

          <Botao
            aparencia="secundaria"
            carregando={salvando}
            onClick={() =>
              void executar(() =>
                api.descreverDossie(emFoco.id, {
                  tipo: tipo === "" ? null : tipo,
                  construcao: construcao === "" ? null : construcao,
                  capacidadeNominalEmLitros: capacidade === "" ? null : capacidade,
                  massaMaximaDeTrabalhoEmKg: massaMaxima === "" ? null : massaMaxima,
                  taraEmKg: tara === "" ? null : tara,
                  comissionadoEm: comissionadoEm === "" ? null : comissionadoEm,
                }),
              )
            }
          >
            Salvar dossiê
          </Botao>

          {/* ── as transições ──────────────────────────────────────────
              Motivo obrigatório em TODAS, e não só nas negativas: é dado
              crítico pelo Art. 265, que pede razão, autor, data e valor
              anterior. Os outros três a trilha traz sozinha. */}
          <div className="recipientes__transicoes">
            <Campo
              rotulo="Motivo da mudança de estado"
              required
              value={motivo}
              onChange={(e) => definirMotivo(e.target.value)}
              dica="Obrigatório em toda transição — é o 'por quê' que a trilha guarda."
            />
            <div className="recipientes__botoes">
              <Botao
                disabled={motivo === "" || emFoco.estado === "APOSENTADO"}
                carregando={salvando}
                onClick={() => void executar(() => api.qualificar(emFoco.id, motivo))}
              >
                Qualificar
              </Botao>
              <Botao
                aparencia="secundaria"
                disabled={motivo === "" || emFoco.estado === "APOSENTADO"}
                carregando={salvando}
                onClick={() => void executar(() => api.bloquear(emFoco.id, motivo))}
              >
                Bloquear
              </Botao>
              <Botao
                aparencia="texto"
                disabled={motivo === "" || emFoco.estado === "APOSENTADO"}
                carregando={salvando}
                onClick={() => void executar(() => api.aposentar(emFoco.id, motivo))}
              >
                Aposentar
              </Botao>
            </div>
            {!emFoco.dossieCompleto && (
              <p className="recipientes__aviso">
                O dossiê está incompleto, e <strong>qualificar exige que ele esteja
                completo</strong> — não se declara apto o que ninguém mediu. Isso{" "}
                <strong>não impede</strong> o tambor de receber material: o dossiê
                incompleto impede a segunda ocupação, não a primeira.
              </p>
            )}
            {emFoco.estado === "APOSENTADO" && (
              <p className="recipientes__aviso">
                Recipiente aposentado <strong>não volta</strong>, e não é apagado:
                ele continua respondendo pelo histórico de tudo que passou por
                ele, e o código dele nunca vai para outro tambor.
              </p>
            )}
          </div>

          {/* ── o histórico ────────────────────────────────────────────
              A pergunta do Art. 192: "o que já esteve aqui?". Ele atravessa
              CNPJs de propósito, e leva o MATERIAL e o PERÍODO — e nada
              além. Fornecedor, nota fiscal, quantidade e código do lote são
              dado comercial da empresa dona da ocupação e não atravessam: o
              histórico é uma lista, não um atalho. */}
          <h3 className="recipientes__ficha-titulo">O que já esteve aqui</h3>
          {historico.length === 0 ? (
            <p className="recipientes__vazio">
              Nenhuma ocupação registrada neste recipiente.
            </p>
          ) : (
            <ul className="recipientes__historico">
              {historico.map((ocupacao, indice) => (
                <li key={`${ocupacao.material}-${indice}`}>
                  <strong>{ocupacao.material}</strong>
                  {ocupacao.desde !== null && <> · desde {formatar(ocupacao.desde)}</>}
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      )}
    </div>
  );
}

/** Data do calendário, formatada sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
